import { FC } from 'react';

import sect1 from '../../assets/img/resource-providers/sect1_img.png';
import sect2 from '../../assets/img/resource-providers/sect2_img.png';
import sect3 from '../../assets/img/resource-providers/sect3_img.png';
import aboutImg from '../../assets/img/resource-providers/home_about_img.png';
import { ReactComponent as KitchenIcon } from '../../icons/KitchenIcon.svg';
import { ReactComponent as InventoryIcon } from '../../icons/InventoryIcon.svg';
import { ReactComponent as MembershipIcon } from '../../icons/MembershipIcon.svg';
import { ReactComponent as PlanIcon } from '../../icons/PlanIcon.svg';
import { ReactComponent as MessageIcon } from '../../icons/MessageIconSelected.svg';
import { ReactComponent as DocumentationIcon } from '../../icons/documentationIcon.svg';
import {
  FEATURE_SECTIONS,
  HOW_IT_WORKS,
  IMPACT_POINTS,
  PARTNER_LINKS,
  PARTNER_SECTION_IDS,
} from './partnerContent';

const VALUE_PROPS = [
  {
    Icon: KitchenIcon,
    heading: 'Kitchen space',
    description: 'Publish prep stations, suites, and shared lines with the hours you actually want booked.',
  },
  {
    Icon: InventoryIcon,
    heading: 'Equipment & storage',
    description: 'Rent out mixers, smokers, dry shelves, and cold storage as add-ons or standalone listings.',
  },
  {
    Icon: MembershipIcon,
    heading: 'Memberships',
    description: 'Offer tiers with agreements, requirements, and renewals handled for you.',
  },
  {
    Icon: PlanIcon,
    heading: 'Payments & payouts',
    description: 'Stripe-powered checkout, invoices, and transaction history for every booking.',
  },
  {
    Icon: MessageIcon,
    heading: 'Member messaging',
    description: 'Answer questions, share updates, and keep the paper trail attached to each member.',
  },
  {
    Icon: DocumentationIcon,
    heading: 'Compliance checklist',
    description: 'Track licenses, insurance, and documents so your space stays inspection-ready.',
  },
];

const FEATURE_IMAGES = [sect1, sect2, sect3];

/** Below-the-fold marketing sections for the logged-out Partner Dashboard landing. */
export const PartnerMarketingPanel: FC = () => (
  <>
    <section id={PARTNER_SECTION_IDS.value} className="bg-white">
      <div className="mx-auto w-full max-w-[1450px] px-6 py-16 lg:px-12 lg:py-20">
        <p className="page-kicker">Everything you can list</p>
        <h3 className="mt-3 max-w-[720px] font-raleway text-[30px] font-bold leading-tight text-ink lg:text-[38px]">
          One dashboard for every resource you rent out
        </h3>
        <p className="mt-4 max-w-[640px] font-open-sans text-[16px] leading-[1.75] text-[#3F5B64]">
          Most operators start with kitchen hours and quickly add the rest. Each listing carries its own pricing,
          availability, and requirements, so you are never running a second spreadsheet.
        </p>

        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {VALUE_PROPS.map(({ Icon, heading, description }) => (
            <div key={heading} className="partner-section-card group p-6">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-tint">
                <Icon className="h-[22px] w-[22px] text-primary-300" />
              </span>
              <p className="mt-4 font-raleway text-[18px] font-semibold text-ink">{heading}</p>
              <p className="mt-2 font-open-sans text-[14px] leading-relaxed text-muted">{description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section id={PARTNER_SECTION_IDS.how} className="bg-primary-tint">
      <div className="mx-auto w-full max-w-[1450px] px-6 py-16 lg:px-12 lg:py-20">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="page-kicker">How it works</p>
            <h3 className="mt-3 max-w-[620px] font-raleway text-[30px] font-bold leading-tight text-ink lg:text-[38px]">
              From empty calendar to booked revenue in four steps
            </h3>
          </div>
          <a
            href={PARTNER_LINKS.bookDemo}
            target="_blank"
            rel="noreferrer"
            className="inline-flex h-12 w-fit items-center rounded-lg bg-primaryDark-0 px-6 font-raleway text-sm font-semibold text-white transition-colors hover:bg-primary-300"
          >
            Walk through it with us
          </a>
        </div>

        <ol className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {HOW_IT_WORKS.map((item) => (
            <li key={item.step} className="relative overflow-hidden rounded-2xl border border-[#E4EEF1] bg-white p-6">
              <span
                aria-hidden
                className="absolute -right-2 -top-4 font-raleway text-[76px] font-bold leading-none text-primary-400/15"
              >
                {item.step}
              </span>
              <span className="relative flex h-9 w-9 items-center justify-center rounded-full bg-primaryDark-0 font-raleway text-sm font-bold text-white">
                {item.step}
              </span>
              <p className="relative mt-4 font-raleway text-[17px] font-semibold text-ink">{item.heading}</p>
              <p className="relative mt-2 font-open-sans text-[14px] leading-relaxed text-muted">{item.description}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>

    <section className="bg-[#F8F8F8]">
      <div className="mx-auto flex w-full max-w-[1450px] flex-col gap-16 px-6 py-16 lg:gap-24 lg:px-12 lg:py-24">
        {FEATURE_SECTIONS.map((feature, index) => {
          const isReversed = index % 2 === 1;
          return (
            <div
              key={feature.id}
              className={`flex flex-col items-center gap-8 lg:gap-12 ${isReversed ? 'lg:flex-row-reverse' : 'lg:flex-row'}`}
            >
              <div className="relative w-full lg:w-[46%]">
                <div
                  aria-hidden
                  className={`absolute -bottom-4 h-full w-full rounded-2xl bg-primary-tint ${isReversed ? '-left-4' : '-right-4'}`}
                />
                {/* <div
                  aria-hidden
                  className={`absolute -top-3 h-full w-[8px] rounded-full bg-primary-400 ${isReversed ? 'right-0' : 'left-0'}`}
                /> */}
                <img
                  src={FEATURE_IMAGES[index]}
                  alt={feature.heading}
                  loading="lazy"
                  className="relative h-[260px] w-full rounded-2xl border border-[#E4EEF1] object-cover shadow-[0_16px_36px_rgba(1,56,71,0.10)] lg:h-[340px]"
                />
              </div>

              <div className="w-full lg:w-[54%]">
                <p className="page-kicker">{feature.kicker}</p>
                <h3 className="mt-3 font-raleway text-[30px] font-bold leading-tight text-ink xl:whitespace-nowrap lg:text-[40px]">
                  <span className="text-primary-300">{feature.accent}</span>
                  {feature.heading.slice(feature.accent.length)}
                </h3>
                <p className="mt-4 max-w-[42rem] font-open-sans text-[16px] leading-[1.75] text-[#3F5B64]">{feature.description}</p>
                <ul className="mt-5 space-y-2.5">
                  {feature.bullets.map((bullet) => (
                    <li key={bullet} className="flex items-start gap-3 font-open-sans text-[15px] text-ink">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-tint font-open-sans text-[11px] font-bold text-primary-300">
                        ✓
                      </span>
                      {bullet}
                    </li>
                  ))}
                </ul>
                <a
                  href={PARTNER_LINKS.bookDemo}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-6 inline-flex h-11 items-center rounded-lg bg-primaryDark-0 px-5 font-raleway text-sm font-semibold text-white transition-colors hover:bg-primary-300"
                >
                  Book a demo
                </a>
              </div>
            </div>
          );
        })}
      </div>
    </section>

    <section id={PARTNER_SECTION_IDS.impact} className="bg-white px-6 pb-16 lg:px-12 lg:pb-24">
      <div className="relative mx-auto w-full max-w-[1450px] overflow-hidden rounded-3xl bg-primaryDark-0">
        <img
          src={aboutImg}
          alt=""
          aria-hidden
          loading="lazy"
          className="pointer-events-none absolute inset-y-0 right-0 hidden h-full w-1/2 object-cover opacity-25 lg:block"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-gradient-to-r from-primaryDark-0 via-primaryDark-0/95 to-primaryDark-0/60"
        />
        <div className="relative grid gap-10 p-8 lg:grid-cols-[1fr_0.9fr] lg:p-14">
          <div>
            <p className="font-raleway text-[12px] font-bold uppercase tracking-[0.16em] text-primary-400">
              Why partners and funders back this
            </p>
            <h3 className="mt-3 max-w-[520px] font-raleway text-[28px] font-bold leading-tight text-white lg:text-[36px]">
              Infrastructure for the food businesses that cannot afford their own kitchen
            </h3>
            <p className="mt-4 max-w-[520px] font-open-sans text-[16px] leading-[1.75] text-white/75">
              Every booking puts an underused commercial space to work and gives a food entrepreneur a
              compliant space to produce. Operators grow revenue, programs get reporting, and founders get a runway.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <a
                href={PARTNER_LINKS.bookDemo}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-12 items-center rounded-lg bg-white px-6 font-raleway text-sm font-semibold text-primaryDark-0 transition-colors hover:bg-primary-400 hover:text-white"
              >
                Book a demo
              </a>
              <a
                href={PARTNER_LINKS.contact}
                className="inline-flex h-12 items-center rounded-lg border border-white/35 px-6 font-raleway text-sm font-semibold text-white transition-colors hover:border-primary-400 hover:text-primary-400"
              >
                Talk to the team
              </a>
            </div>
          </div>

          <ul className="grid gap-4 self-center">
            {IMPACT_POINTS.map((point) => (
              <li key={point.label} className="rounded-2xl border border-white/15 bg-white/[0.06] p-5">
                <p className="font-raleway text-[17px] font-semibold text-white">{point.label}</p>
                <p className="mt-1.5 font-open-sans text-[14px] leading-relaxed text-white/70">{point.description}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>

    <section className="border-t border-[#E4EEF1] bg-primary-tint">
      <div className="mx-auto flex w-full max-w-[1450px] flex-col items-center gap-5 px-6 py-14 text-center lg:px-12">
        <p className="page-kicker">Ready when you are</p>
        <h3 className="max-w-[680px] font-raleway text-[28px] font-bold leading-tight text-ink lg:text-[36px]">
          List your resources and start taking bookings today
        </h3>
        <p className="max-w-[560px] font-open-sans text-[16px] leading-[1.75] text-[#3F5B64]">
          Create your partner account in a few minutes, or have us walk through the dashboard with you first.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <a
            href={`#${PARTNER_SECTION_IDS.auth}`}
            className="inline-flex h-12 items-center rounded-lg bg-primaryDark-0 px-6 font-raleway text-sm font-semibold text-white transition-colors hover:bg-primary-300"
          >
            Get started
          </a>
          <a
            href={PARTNER_LINKS.bookDemo}
            target="_blank"
            rel="noreferrer"
            className="inline-flex h-12 items-center rounded-lg border border-[#D5E3E8] bg-white px-6 font-raleway text-sm font-semibold text-ink transition-colors hover:border-primary-400"
          >
            Book a demo
          </a>
        </div>
      </div>
    </section>
  </>
);

export default PartnerMarketingPanel;
